import React, { useEffect, useState, useRef, useCallback } from 'react';
import baseURL from '../../url';
import moneda from '../../moneda';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell,
    faCheckCircle,
    faUtensils,
    faClock,
    faSync,
    faPlus,
    faEye,
    faExclamationCircle,
    faUserTie
} from '@fortawesome/free-solid-svg-icons';
import { playSound } from '../../../utils/soundService';
import { getConfig } from '../../../utils/configService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import './MeserosData.css';

export default function MeserosData() {
    const [llamadas, setLlamadas] = useState([]);
    const [mesas, setMesas] = useState([]);
    const [pedidos, setPedidos] = useState([]);
    const [productos, setProductos] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal para tomar nuevo pedido desde mesa
    const [modalPedidoMesa, setModalPedidoMesa] = useState(null); // mesa seleccionada
    const [cartMesero, setCartMesero] = useState([]);
    const [nombreCliente, setNombreCliente] = useState('Cliente Mesa');
    const [notaMesero, setNotaMesero] = useState('');
    const [guardandoPedido, setGuardandoPedido] = useState(false);

    // Llamada seleccionada para atención detallada
    const [llamadaSeleccionada, setLlamadaSeleccionada] = useState(null);

    const prevCallIdsRef = useRef(new Set());
    const isFirstLoadRef = useRef(true);

    const cargarMesas = async () => {
        try {
            const res = await fetch(`${baseURL}/mesaGet.php`);
            const data = await res.json();
            setMesas(data.mesas || []);
        } catch (e) {
            console.error('Error al cargar mesas:', e);
        }
    };

    const cargarPedidos = async () => {
        try {
            const res = await fetch(`${baseURL}/pedidoGet.php`);
            const data = await res.json();
            setPedidos(data.pedidos || []);
        } catch (e) {
            console.error('Error al cargar pedidos:', e);
        }
    };

    const cargarProductos = async () => {
        try {
            const res = await fetch(`${baseURL}/productosGet.php`);
            const data = await res.json();
            setProductos(data.productos || []);
        } catch (e) {
            console.error('Error al cargar productos:', e);
        }
    };

    const cargarLlamadas = useCallback(async () => {
        try {
            const res = await fetch(`${baseURL}/llamadaMeseroGet.php?estado=pendiente`);
            const data = await res.json();
            const pendientes = data.llamadas || [];

            // Detectar si hay llamadas nuevas no sonadas
            if (!isFirstLoadRef.current) {
                const nuevas = pendientes.filter(l => !prevCallIdsRef.current.has(l.idLlamada));
                if (nuevas.length > 0) {
                    const cfg = getConfig();
                    playSound(cfg.sonidoLlamadaMesero || 'ring_2');
                    toast.info(`🔔 ¡Llamada de cliente en ${nuevas[0].mesa}! Motivo: ${nuevas[0].motivo}`, {
                        position: 'top-right',
                        autoClose: 5000
                    });
                }
            } else {
                isFirstLoadRef.current = false;
            }

            prevCallIdsRef.current = new Set(pendientes.map(l => l.idLlamada));
            setLlamadas(pendientes);
            setLoading(false);
        } catch (e) {
            console.error('Error al cargar llamadas:', e);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        cargarMesas();
        cargarPedidos();
        cargarProductos();
        cargarLlamadas();

        const interval = setInterval(() => {
            cargarLlamadas();
            cargarPedidos();
            cargarMesas();
        }, 5000);

        return () => clearInterval(interval);
    }, [cargarLlamadas]);

    // Atender llamada
    const atenderLlamada = async (idLlamada) => {
        try {
            const res = await fetch(`${baseURL}/llamadaMeseroPut.php?idLlamada=${idLlamada}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ estado: 'atendida' })
            });
            const data = await res.json();
            if (data.mensaje) {
                toast.success('Solicitud marcada como atendida');
                setLlamadas(prev => prev.filter(l => l.idLlamada !== idLlamada));
                setLlamadaSeleccionada(null);
            } else {
                toast.error(data.error || 'Error al actualizar');
            }
        } catch (e) {
            console.error(e);
            toast.error('Error de red al atender solicitud');
        }
    };

    // Agregar producto al carrito del mesero
    const agregarAlPedidoMesero = (prod) => {
        setCartMesero(prev => {
            const existing = prev.find(p => p.idProducto === prod.idProducto);
            if (existing) {
                return prev.map(p => p.idProducto === prod.idProducto ? { ...p, cantidad: p.cantidad + 1 } : p);
            }
            return [...prev, {
                idProducto: prod.idProducto,
                titulo: prod.titulo,
                precio: Number(prod.precio) || 0,
                cantidad: 1,
                categoria: prod.categoria,
                imagen: prod.imagen1 || ''
            }];
        });
    };

    const modificarCantidadMesero = (idProducto, delta) => {
        setCartMesero(prev => {
            return prev.map(p => {
                if (p.idProducto === idProducto) {
                    const n = p.cantidad + delta;
                    return n > 0 ? { ...p, cantidad: n } : null;
                }
                return p;
            }).filter(Boolean);
        });
    };

    // Guardar pedido registrado por mesero
    const handleCrearPedidoMesero = async () => {
        if (!modalPedidoMesa) return;
        if (cartMesero.length === 0) {
            toast.warn('Agrega al menos un producto');
            return;
        }

        setGuardandoPedido(true);
        try {
            const usuarioActual = JSON.parse(localStorage.getItem('usuario') || '{}');
            const total = cartMesero.reduce((acc, p) => acc + (p.precio * p.cantidad), 0);

            const payload = {
                idMesa: modalPedidoMesa.idMesa,
                nombre: nombreCliente || `Mesa ${modalPedidoMesa.mesa}`,
                nota: notaMesero,
                estado: 'Pendiente', // Enviado a cocina
                origen: 'mesero',
                tipoEntrega: 'mesa',
                metodoPago: 'efectivo',
                estadoPago: 'pendiente',
                idUsuario: usuarioActual.idUsuario || null,
                total: total,
                productos: JSON.stringify(cartMesero)
            };

            const res = await fetch(`${baseURL}/pedidoPost.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            setGuardandoPedido(false);

            if (data.mensaje) {
                Swal.fire({
                    title: '¡Pedido enviado a cocina!',
                    text: `El pedido para ${modalPedidoMesa.mesa} ha sido enviado correctamente.`,
                    icon: 'success'
                });
                setModalPedidoMesa(null);
                setCartMesero([]);
                setNotaMesero('');
                cargarPedidos();
                cargarMesas();
            } else {
                toast.error(data.error || 'Error al guardar pedido');
            }
        } catch (e) {
            setGuardandoPedido(false);
            console.error(e);
            toast.error('Error de red al guardar pedido');
        }
    };

    return (
        <div className="meseros-container">
            <ToastContainer />

            {/* Header del Panel */}
            <div className="meseros-topbar">
                <div className="meseros-title">
                    <h2>🤵 Panel de Atención de Meseros</h2>
                    <span>Atención de llamadas de clientes y registro rápido de comandas</span>
                </div>
                <button className="meseros-reload-btn" onClick={() => { cargarLlamadas(); cargarMesas(); cargarPedidos(); }}>
                    <FontAwesomeIcon icon={faSync} /> Recargar
                </button>
            </div>

            {/* SECCIÓN 1: Solicitudes y Llamadas Activas */}
            <div className="meseros-llamadas-box">
                <div className="llamadas-box-header">
                    <div className="llamadas-title-flex">
                        <FontAwesomeIcon icon={faBell} className="bell-pulse" />
                        <h3>Solicitudes de Clientes</h3>
                    </div>
                    <span className="llamadas-count-badge">
                        {llamadas.length} {llamadas.length === 1 ? 'pendiente' : 'pendientes'}
                    </span>
                </div>

                {llamadas.length === 0 ? (
                    <div className="no-llamadas-msg">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        <p>No hay solicitudes de clientes pendientes en este momento.</p>
                    </div>
                ) : (
                    <div className="llamadas-grid">
                        {llamadas.map(ll => (
                            <div key={ll.idLlamada} className="llamada-card">
                                <div className="llamada-card-header">
                                    <span className="llamada-mesa-badge">🍽️ {ll.mesa}</span>
                                    <span className="llamada-time">
                                        <FontAwesomeIcon icon={faClock} /> {ll.createdAt?.split(' ')[1] || ll.createdAt}
                                    </span>
                                </div>
                                <div className="llamada-motivo">
                                    <strong>Motivo:</strong> {ll.motivo || 'Atención general'}
                                </div>
                                <div className="llamada-actions">
                                    <button
                                        className="btn-atender-llamada"
                                        onClick={() => atenderLlamada(ll.idLlamada)}
                                    >
                                        <FontAwesomeIcon icon={faCheckCircle} /> Marcar Atendida
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* SECCIÓN 2: Mesas del Restaurante y Estado */}
            <div className="meseros-mesas-section">
                <div className="mesas-sec-header">
                    <h3>🍽️ Estado de Mesas</h3>
                    <p>Selecciona una mesa para tomar comandas o revisar pedidos activos.</p>
                </div>

                <div className="meseros-mesas-grid">
                    {mesas.map(m => {
                        const pedidoMesa = pedidos.find(p => p.idMesa === m.idMesa && p.estado !== 'Pagado' && p.estado !== 'Rechazado');
                        const tieneLlamada = llamadas.some(l => l.idMesa === m.idMesa);

                        return (
                            <div
                                key={m.idMesa}
                                className={`mesero-mesa-card ${m.estado === 'libre' ? 'mesa-libre' : 'mesa-ocupada'} ${tieneLlamada ? 'mesa-alert-pulse' : ''}`}
                            >
                                <div className="mesa-card-top-row">
                                    <h4>{m.mesa}</h4>
                                    <span className={`mesa-status-tag ${m.estado}`}>
                                        {m.estado}
                                    </span>
                                </div>

                                {tieneLlamada && (
                                    <div className="mesa-calling-badge">
                                        <FontAwesomeIcon icon={faBell} /> ¡Llamando al mesero!
                                    </div>
                                )}

                                {pedidoMesa ? (
                                    <div className="mesa-order-preview">
                                        <span className="order-id-label">Pedido #{pedidoMesa.idPedido}</span>
                                        <span className="order-state-label">{pedidoMesa.estado}</span>
                                        <span className="order-total-label">{moneda} {pedidoMesa.total}</span>
                                    </div>
                                ) : (
                                    <div className="mesa-empty-preview">
                                        Sin pedidos activos
                                    </div>
                                )}

                                <div className="mesa-card-bottom-actions">
                                    <button
                                        className="btn-tomar-pedido-mesa"
                                        onClick={() => {
                                            setModalPedidoMesa(m);
                                            setCartMesero([]);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPlus} /> Tomar Pedido
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modal para Tomar Pedido en Mesa */}
            {modalPedidoMesa && (
                <div className="modal modal-mesero-order-overlay">
                    <div className="modal-content modal-mesero-order-content">
                        <div className="deFlexBtnsModal">
                            <h3>📝 Tomar Pedido - {modalPedidoMesa.mesa}</h3>
                            <span className="close" onClick={() => setModalPedidoMesa(null)}>&times;</span>
                        </div>

                        <div className="mesero-order-layout">
                            {/* Catálogo de Productos */}
                            <div className="mesero-order-catalog">
                                <h4>Selecciona productos:</h4>
                                <div className="mesero-prods-list">
                                    {productos.map(p => (
                                        <div
                                            key={p.idProducto}
                                            className="mesero-prod-chip"
                                            onClick={() => agregarAlPedidoMesero(p)}
                                        >
                                            <div className="mesero-prod-chip-info">
                                                <strong>{p.titulo}</strong>
                                                <span>{moneda} {p.precio}</span>
                                            </div>
                                            <button className="mesero-add-btn">
                                                <FontAwesomeIcon icon={faPlus} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Ticket actual */}
                            <div className="mesero-order-ticket">
                                <h4>Comanda actual:</h4>
                                <div className="mesero-inputs-row">
                                    <input
                                        type="text"
                                        placeholder="Nombre del cliente"
                                        value={nombreCliente}
                                        onChange={(e) => setNombreCliente(e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Observaciones / Notas"
                                        value={notaMesero}
                                        onChange={(e) => setNotaMesero(e.target.value)}
                                    />
                                </div>

                                <div className="mesero-ticket-items">
                                    {cartMesero.length === 0 ? (
                                        <p className="empty-ticket-text">Agrega productos a la comanda</p>
                                    ) : (
                                        cartMesero.map(item => (
                                            <div key={item.idProducto} className="mesero-ticket-row">
                                                <div className="ticket-prod-desc">
                                                    <strong>{item.titulo}</strong>
                                                    <span>{moneda} {item.precio}</span>
                                                </div>
                                                <div className="ticket-qty-group">
                                                    <button onClick={() => modificarCantidadMesero(item.idProducto, -1)}>-</button>
                                                    <span>{item.cantidad}</span>
                                                    <button onClick={() => modificarCantidadMesero(item.idProducto, 1)}>+</button>
                                                </div>
                                                <strong>{moneda} {(item.precio * item.cantidad).toFixed(2)}</strong>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <div className="mesero-ticket-total">
                                    <span>Total:</span>
                                    <strong>{moneda} {cartMesero.reduce((acc, p) => acc + (p.precio * p.cantidad), 0).toFixed(2)}</strong>
                                </div>

                                <button
                                    className="btn-enviar-comanda-cocina"
                                    onClick={handleCrearPedidoMesero}
                                    disabled={cartMesero.length === 0 || guardandoPedido}
                                >
                                    {guardandoPedido ? 'Enviando comanda...' : '🍳 Enviar Pedido a Cocina'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
